import { Box, FormControl } from "@primer/react";
import { useMemo } from "react";
import DataGrid from "../../components/ui/data-grid";
import { IDataGridColumn } from "../../components/ui/data-grid/type/IDataGridProps";
import { IMeetingSlot, SlotStatus } from "../../models/response/meeting/IMeetingSlot";
import { IMeetingVm } from "../../models/response/meeting/IMeetingVm";
import { Dispatch, SetStateAction } from "react";

interface Props {
    meeting: IMeetingVm;
    setMeeting: Dispatch<SetStateAction<IMeetingVm>>;
    errors: any;
    labelR?: string;
    isGroupByTeacher?: boolean;
}

const MeetingSchedule = ({
    meeting,
    setMeeting,
    errors,
    labelR,
    isGroupByTeacher
}: Props) => {

    // =========================
    // FORMAT
    // =========================
    const formatDate = (date: string) => {
        return new Date(date).toLocaleDateString("vi-VN");
    };

    const formatTime = (date: string) => {
        const d = new Date(date);
        if (isNaN(d.getTime())) return "--:--";
        return d.toTimeString().slice(0, 5);
    };

    const formatRange = (start: string, duration: number) => {
        const s = new Date(start);
        if (isNaN(s.getTime())) return "";

        const e = new Date(s.getTime() + (duration || 0) * 60000);

        return `${formatTime(s.toISOString())}`;
    };

    // =========================
    // GROUP DATE
    // =========================
    const dates = useMemo(() => {
        const set = new Set<string>();

        (meeting?.slots ?? []).forEach(slot => {
            const d = new Date(slot.start_at);
            if (!isNaN(d.getTime())) {
                set.add(d.toISOString().split("T")[0]);
            }
        });

        return Array.from(set).sort();
    }, [meeting.slots]);

    // =========================
    // TOGGLE SLOT
    // =========================
    const toggleSlot = (slot: IMeetingSlot) => {
        if (slot.status === SlotStatus.Booked) return;

        setMeeting(prev => ({
            ...prev,
            slots: prev.slots.map(s =>
                s.id === slot.id &&
                    s.sis_giaovien_id === slot.sis_giaovien_id
                    ? {
                        ...s,
                        status:
                            s.status === SlotStatus.Free
                                ? SlotStatus.Busy
                                : SlotStatus.Free
                    }
                    : s
            )
        }));
    };

    // =========================
    // RENDER SLOT UI
    // =========================
    const renderSlots = (slots: IMeetingSlot[]) => {
        if (!slots || slots.length === 0) return null;

        return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                {slots.map((slot) => (
                    <Box
                        key={slot.id}
                        title={`${formatRange(slot.start_at, slot.duration_in_minute)} | ${slot.status === SlotStatus.Free
                                ? "Trống"
                                : slot.status === SlotStatus.Busy
                                    ? "Lịch bận"
                                    : "Đã đăng ký"
                            }`}
                        sx={{
                            width: "8px",
                            height: "18px",
                            borderRadius: "3px",
                            border: "1px solid #d0d7de",
                            cursor:
                                slot.status === SlotStatus.Booked
                                    ? "not-allowed"
                                    : "pointer",
                            backgroundColor:
                                slot.status === SlotStatus.Free
                                    ? "#eaeef2"
                                    : slot.status === SlotStatus.Busy
                                        ? "#e69790"
                                        : "#78f092"
                        }}
                        onClick={() => toggleSlot(slot)}
                    />
                ))}
            </Box>
        );
    };

    // =========================
    // BUILD DATASOURCE
    // =========================
    const dataSource = useMemo(() => {
        if (!meeting?.slots) return [];

        const map: Record<string, any> = {};

        meeting.slots.forEach(slot => {
            const teacherKey = isGroupByTeacher
                ? slot.sis_giaovien_id
                : "all";

            if (!map[teacherKey]) {
                map[teacherKey] = {
                    teacher: isGroupByTeacher
                        ? slot.sis_giaovien_name || "Không có tên"
                        : labelR || ""
                };
            }

            const d = new Date(slot.start_at);
            if (isNaN(d.getTime())) return;

            const dateKey = `date_${d.toISOString().split("T")[0]}`;

            if (!map[teacherKey][dateKey]) {
                map[teacherKey][dateKey] = [];
            }

            map[teacherKey][dateKey].push(slot);
        });

        return Object.values(map);
    }, [meeting.slots, isGroupByTeacher, labelR]);

    // =========================
    // BUILD COLUMNS
    // =========================
    const columns: IDataGridColumn[] = useMemo(() => {
        return [
            {
                dataField: "teacher",
                caption: isGroupByTeacher ? "Giáo viên" : "",
                width: 180,
                fixed: "left"
            },
            ...dates.map(date => ({
                dataField: `date_${date}`,
                caption: formatDate(date),
                width: 260,
                cellRender: (row: any) => {
                    return renderSlots(row[`date_${date}`]);
                }
            }))
        ];
    }, [dates, isGroupByTeacher]);

    // =========================
    // RENDER
    // =========================
    return (
        <>
            <Box>
                <DataGrid
                    columns={columns}
                    dataSource={dataSource}
                    searchEnable={false}
                />
            </Box>

            {errors?.slots && (
                <FormControl>
                    <FormControl.Validation variant="error">
                        Vui lòng điền lịch họp
                    </FormControl.Validation>
                </FormControl>
            )}
        </>
    );
};

export default MeetingSchedule;